import TileLayer from 'ol/layer/Tile';
import { Map } from 'ol';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import { boundingExtent } from 'ol/extent';
import { Subject, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Coordinate } from 'ol/coordinate';
import { transform, transformExtent } from 'ol/proj';
import { Site } from '../../../shared/models/site';
import { TileWMS } from 'ol/source';
import MapConstants from '../models/map-constants';
import { MapHttpService } from '../services/map-service';
export enum MAP_VIEW_TYPE {
  ol,
  mapbox,
  geoserver,
}

const MAPBOX_MAP_EXTENT = 10000; //meters

// function transformLocal(extent: number[]) {
//   return transformExtent(extent, 'EPSG:4326', 'EPSG:3857');
// }
// const extents = {
//   India: transformLocal([68.17665, 7.96553, 97.40256, 35.49401]),
//   Argentina: transformLocal([-73.41544, -55.25, -53.62835, -21.83231]),
//   Nigeria: transformLocal([2.6917, 4.24059, 14.57718, 13.86592]),
//   Sweden: transformLocal([11.02737, 55.36174, 23.90338, 69.10625]),
// };

export class MapLayersControler {
  private http: MapHttpService;
  private site: Site | undefined;
  private transformedSiteCoord: Coordinate = [];
  private map: Map;
  private authkey?: string = '';
  private mapboxAccessToken: string = '';
  private hostGeoServer: string = '';
  private unsubscribe$ = new Subject<void>();
  private stylesMapbox: string[] = [
    'satellite-v9',
    // 'streets-v11',
    // 'outdoors-v11',
    // 'light-v10',
    // 'dark-v10',
  ];

  private osmLayer: TileLayer<OSM> | undefined;
  // private geoserverLayer?: TileLayer<any> = undefined;
  private mapboxLayer?: TileLayer<any> = undefined;
  public isOrthoLayerVisible: boolean = false;
  public isMapboxLayerVisible: boolean = false;
  public isOSMLayerVisible: boolean = true;

  constructor(map: Map, http: MapHttpService, site: Site) {
    this.http = http;
    this.map = map;
    this.site = site;
    this.createOSMLayer()
  }

  public changeSite(site: Site) {
    this.site = site;
    //'setExtent':  is used to display only the square, around the site
    if (null !== this.mapboxLayer && undefined !== this.mapboxLayer) {
      this.mapboxLayer.setExtent(this.getExtendBounds());
    }
  }

  public toggleLayer(layer: MAP_VIEW_TYPE, visible: boolean) {
    if (!this.osmLayer) {
      return;
    }

    switch (layer) {
      case MAP_VIEW_TYPE.ol:
        this.osmLayer.setVisible(visible);
        this.isOSMLayerVisible = visible;
        break;
      case MAP_VIEW_TYPE.mapbox:
        // it will create only if there is a first time click
        this.createMapboxLayer().then(() => {
          this.mapboxLayer!.setVisible(visible);
          this.isMapboxLayerVisible = visible;
        })
        break;
    }
  }

  //create, add basic map layer
  private createOSMLayer() {
    this.osmLayer = new TileLayer({
      source: new OSM(),
    });
    this.osmLayer.setZIndex(0);
    this.map.addLayer(this.osmLayer);
  }


  //create, add mapbox layer
  public async createMapboxLayer(): Promise<void> {
    // If the layer already exists, resolve immediately
    if (this.mapboxLayer) return Promise.resolve();

    try {
      // Fetch the Mapbox access token from the service
      this.mapboxAccessToken = await this.http.getMapBoxPublicKey();
      
      if (!this.mapboxAccessToken) {
        throw new Error('Mapbox access token is invalid or missing.');
      }
      
      // Create and configure the Mapbox layer
      this.mapboxLayer = new TileLayer({
        extent: this.getExtendBounds() ?? [], // Use the provided extent
        visible: true,
        preload: 1,
        source: new XYZ({
          url: `https://api.mapbox.com/styles/v1/mapbox/${this.stylesMapbox[0]}/tiles/256/{z}/{x}/{y}?access_token=${this.mapboxAccessToken}`,
          attributions: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
        }),
      });

      this.mapboxLayer.setZIndex(1);
      this.map.addLayer(this.mapboxLayer);
    } catch (error) {
      console.log("Error while creating Mapbox layer: " + error);
      throw error; // Re-throw the error for proper promise handling
    }
  }


  //crée la couche raster en faisant un appel au GeoServer
  private createLayerGeoserver(): TileLayer<any> | null {
    if (this.hostGeoServer != '' && null !== this.authkey) {
      var orthoLayer = new TileLayer({
        source: undefined,
      });

      return orthoLayer;
    } else return null;
  }

  private changeGeoserverSource(): TileWMS | null {
    if (null !== this.authkey && undefined !== this.authkey && this.site) {
      return new TileWMS({
        url: this.hostGeoServer + 'wms?authkey=' + this.authkey,
        params: {
          LAYERS: 'EPC_' + this.site.siteId + ':GROUP_' + this.site.siteId,
          TILED: true,
          //'FORMAT': 'image/jpeg'
        },
        serverType: 'geoserver',
      });
    } else {
      return null;
    }
  }



  private transformCoordSite() {
    if (
      this.site &&
      null != this.site.coordX &&
      undefined != this.site.coordX &&
      null != this.site.coordY &&
      undefined != this.site.coordY
    ) {
      const coordSite = [this.site.coordX, this.site.coordY];
      return transform(
        coordSite,
        MapConstants.EPSG_WGS84,
        MapConstants.EPSG_REFERENCE_OL
      );
    } else {
      return undefined;
    }
  }

  private getExtendBounds(): number[] {
    //epsg of all cordinates in the database Explore is: 4326,
    //therefore must always be transformed to: 3857, before displaying
    const centerCoordinates = this.transformCoordSite() ?? [];
    const x = centerCoordinates[0];
    const y = centerCoordinates[1];
    return boundingExtent([
      [x - MAPBOX_MAP_EXTENT, y - MAPBOX_MAP_EXTENT],
      [x + MAPBOX_MAP_EXTENT, y + MAPBOX_MAP_EXTENT],
    ]);
  }

  public destroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
